import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Snowflake } from "lucide-react";
import { authApi } from "../../lib/api";
import { toast } from "sonner";

/**
 * AuthCallback - handles the redirect from Emergent Auth.
 * URL fragment looks like: /admin/auth/callback#session_id=xxxx
 * Exchanges session_id with backend (which sets the httpOnly cookie)
 * then navigates to /admin.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      toast.error("Missing session. Please try logging in again.");
      navigate("/admin/login", { replace: true });
      return;
    }

    authApi
      .exchangeGoogleSession(sessionId)
      .then(() => {
        toast.success("Signed in with Google");
        navigate("/admin", { replace: true });
      })
      .catch((err) => {
        const detail = err.response?.data?.detail || "Sign-in failed";
        toast.error(detail);
        navigate("/admin/login", { replace: true });
      });
  }, [location.hash, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="text-center">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-400 to-cyan-300 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-sky-200/50">
          <Snowflake className="w-7 h-7 text-white animate-spin" />
        </div>
        <p className="text-slate-600 font-medium">Signing you in…</p>
      </div>
    </div>
  );
}
